import { createImageVariantConfig } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

// ================ Action types ================ //

export const FETCH_FAVORITES_REQUEST = 'app/FavoritesPage/FETCH_FAVORITES_REQUEST';
export const FETCH_FAVORITES_SUCCESS = 'app/FavoritesPage/FETCH_FAVORITES_SUCCESS';
export const FETCH_FAVORITES_ERROR = 'app/FavoritesPage/FETCH_FAVORITES_ERROR';

// ================ Reducer ================ //

const initialState = {
  favorites: [],
  fetchFavoritesInProgress: false,
  fetchFavoritesError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_FAVORITES_REQUEST:
      return {
        ...state,
        fetchFavoritesInProgress: true,
        fetchFavoritesError: null,
      };
    case FETCH_FAVORITES_SUCCESS:
      return {
        ...state,
        favorites: payload,
        fetchFavoritesInProgress: false,
      };
    case FETCH_FAVORITES_ERROR:
      return {
        ...state,
        fetchFavoritesInProgress: false,
        fetchFavoritesError: payload,
      };
    default:
      return state;
  }
}

// ================ Selectors ================ //

export const getFavorites = state => state.FavoritesPage.favorites;
export const getFetchInProgress = state => state.FavoritesPage.fetchFavoritesInProgress;
export const getFetchError = state => state.FavoritesPage.fetchFavoritesError;

// ================ Action creators ================ //

export const fetchFavoritesRequest = () => ({ type: FETCH_FAVORITES_REQUEST });
export const fetchFavoritesSuccess = favorites => ({
  type: FETCH_FAVORITES_SUCCESS,
  payload: favorites,
});
export const fetchFavoritesError = error => ({
  type: FETCH_FAVORITES_ERROR,
  payload: error,
  error: true,
});

// ================ Thunks ================ //

export const loadData = (config) => {
  return (dispatch, getState, sdk) => {
    dispatch(fetchFavoritesRequest());

    const currentUser = getState().user.currentUser;
    const favoriteListingIds =
      currentUser?.attributes?.profile?.privateData?.favorites || [];

    if (favoriteListingIds.length === 0) {
      dispatch(fetchFavoritesSuccess([]));
      return Promise.resolve([]);
    }

    const {
      aspectWidth = 1,
      aspectHeight = 1,
      variantPrefix = 'listing-card',
    } = config?.layout?.listingImage || {};
    const aspectRatio = aspectHeight / aspectWidth;

    const params = {
      ids: favoriteListingIds,
      include: ['author', 'images'],
      'fields.listing': [
        'title',
        'geolocation',
        'price',
        'deleted',
        'state',
        'publicData.listingType',
        'publicData.transactionProcessAlias',
        'publicData.unitType',
      ],
      'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
      'fields.image': [
        'variants.scaled-small',
        'variants.scaled-medium',
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      'limit.images': 1,
    };

    return sdk.listings
      .query(params)
      .then(response => {
        const listingFields = config?.listing?.listingFields;
        const sanitizeConfig = { listingFields };

        dispatch(addMarketplaceEntities(response, sanitizeConfig));

        const listingIds = response.data.data
          .filter(l => !l.attributes.deleted && l.attributes.state === 'published')
          .map(l => l.id);

        dispatch(fetchFavoritesSuccess(listingIds));
        return response;
      })
      .catch(e => {
        dispatch(fetchFavoritesError(storableError(e)));
        throw e;
      });
  };
};

// Helper thunk to add/remove a listing from favorites
export const toggleFavorite = (listingId, isFavorite) => {
  return (dispatch, getState, sdk) => {
    const currentUser = getState().user.currentUser;
    const currentFavorites =
      currentUser?.attributes?.profile?.privateData?.favorites || [];

    // isFavorite = true means it IS currently favorited, so we REMOVE it
    // isFavorite = false means it is NOT currently favorited, so we ADD it
    const newFavorites = isFavorite
      ? currentFavorites.filter(id => id !== listingId)
      : [...currentFavorites, listingId];

    return sdk.currentUser
      .updateProfile({
        privateData: {
          favorites: newFavorites,
        },
      })
      .then(() => {
        dispatch(fetchCurrentUser());
      })
      .catch(e => {
        console.error('Error toggling favorite:', e);
        throw e;
      });
  };
};
